import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Map as RoadmapsIcon,
  Download as ImportIcon,
  ViewStream as AggregateIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Sort as SortIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon,
  Search as SearchIcon,
  Label as LabelIcon,
  Sync as SyncIcon,
  Layers as PhasesIcon,
  Image as ImageIcon,
  EventNote as EventIcon,
  ArrowDropDown as ArrowDropDownIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import InputAdornment from '@mui/material/InputAdornment';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const ROW_H = 40;
const NODE_COLORS = ['#1565c0', '#2e7d32', '#e65100', '#c62828', '#6a1b9a', '#00838f', '#4e342e', '#ad1457'];
const STATUSES = ['Not Started', 'In Progress', 'At Risk', 'Blocked', 'Complete'];
const STATUS_COLORS = {
  'Not Started': '#616161',
  'In Progress': '#1565c0',
  'At Risk':     '#e65100',
  'Blocked':     '#c62828',
  'Complete':    '#2e7d32',
};

const PHASE_COLORS = ['#1565c0', '#2e7d32', '#e65100', '#6a1b9a', '#00838f', '#c62828', '#4e342e', '#ad1457'];
const ROADMAP_COLORS = ['#1565c0', '#2e7d32', '#e65100', '#6a1b9a', '#00838f', '#c62828', '#4e342e', '#ad1457'];

function newPhase(fromRow = 0) {
  return {
    id: `ph-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: 'Phase',
    from_row: fromRow,
    to_row: fromRow,
    color: PHASE_COLORS[0],
  };
}

const RELEASE_COLORS = ['#c62828', '#6a1b9a', '#1565c0', '#2e7d32', '#e65100', '#00838f'];

function newRelease(date = '') {
  return {
    id: `rel-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    date,
    label: 'Release',
    notes: '',
    color: RELEASE_COLORS[0],
  };
}

// ── ReleaseEditDialog ─────────────────────────────────────────────────────────

function ReleaseEditDialog({ open, release, onSave, onDelete, onClose }) {
  const [local, setLocal] = useState(release || newRelease());

  useEffect(() => { setLocal(release || newRelease()); }, [release]);

  const set = (field, val) => setLocal((prev) => ({ ...prev, [field]: val }));
  const isNew = !release;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isNew ? 'Add Release' : 'Edit Release'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        <TextField
          label="Label"
          value={local.label}
          onChange={(e) => set('label', e.target.value)}
          size="small"
          fullWidth
          autoFocus
        />
        <TextField
          label="Date"
          type="date"
          value={local.date}
          onChange={(e) => set('date', e.target.value)}
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Notes (optional)"
          value={local.notes}
          onChange={(e) => set('notes', e.target.value)}
          size="small"
          fullWidth
          multiline
          rows={2}
        />
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Color</Typography>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {RELEASE_COLORS.map((c) => (
              <Box
                key={c}
                onClick={() => set('color', c)}
                sx={{
                  width: 22, height: 22, borderRadius: '50%', bgcolor: c,
                  cursor: 'pointer',
                  border: local.color === c ? '2px solid #fff' : '2px solid transparent',
                  boxShadow: local.color === c ? `0 0 0 2px ${c}` : 'none',
                }}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        {!isNew && (
          <Button color="error" onClick={() => onDelete(local.id)} sx={{ mr: 'auto' }}>
            Delete
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onSave(local)}
          disabled={!local.label.trim() || !local.date}
        >
          {isNew ? 'Add' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function formatBudget(budget) {
  if (!budget) return null;
  const total = (budget.capex || 0) + (budget.opex || 0);
  if (total <= 0) return null;
  return `$${(total / 1000).toFixed(0)}k`;
}

// ── Date helpers ──────────────────────────────────────────────────────────────

function toMs(dateStr) {
  return new Date(dateStr + 'T00:00:00').getTime();
}

function toYMD(date) {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayStr() {
  return toYMD(new Date());
}

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return toYMD(d);
}

function formatDisplayDate(dateStr) {
  try {
    return new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
      .format(new Date(dateStr + 'T00:00:00'));
  } catch {
    return dateStr;
  }
}

function getTimeRange(nodes) {
  if (!nodes || !nodes.length) {
    const yr = new Date().getFullYear();
    return {
      startMs: new Date(`${yr}-01-01T00:00:00`).getTime(),
      endMs: new Date(`${yr}-12-31T00:00:00`).getTime(),
    };
  }
  const starts = nodes.map((n) => toMs(n.start_date));
  const ends = nodes.map((n) => toMs(n.end_date));
  return {
    startMs: Math.min(...starts),
    endMs: Math.max(...ends),
  };
}

function getMonthHeaders(startMs, endMs) {
  const totalMs = endMs - startMs;
  const headers = [];
  const cursor = new Date(startMs);
  cursor.setDate(1);
  while (cursor.getTime() < endMs) {
    const mStart = Math.max(cursor.getTime(), startMs);
    const nextMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    const mEnd = Math.min(nextMonth.getTime(), endMs);
    headers.push({
      label: cursor.toLocaleString('default', { month: 'short', year: '2-digit' }),
      leftPct: ((mStart - startMs) / totalMs) * 100,
      widthPct: ((mEnd - mStart) / totalMs) * 100,
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return headers;
}

function newNode(row = 0) {
  const start = todayStr();
  return {
    id: `node-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: 'New Item',
    start_date: start,
    end_date: addDays(start, 30),
    row,
    color: NODE_COLORS[row % NODE_COLORS.length],
    notes: '',
    doc_links: [],
    app_links: [],
    outcome_links: [],
    data_map_links: [],
    depends_on: [],
    status: 'Not Started',
    owner_id: '',
    milestone_id: null,
    outcome_id: null,
  };
}

function autoArrange(nodes) {
  const sorted = [...nodes].sort((a, b) => toMs(a.start_date) - toMs(b.start_date));
  const rowEnds = []; // rowEnds[i] = latest endMs in row i
  return sorted.map((node) => {
    const nodeStart = toMs(node.start_date);
    const nodeEnd = toMs(node.end_date);
    const rowIdx = rowEnds.findIndex((end) => end <= nodeStart);
    const assignedRow = rowIdx >= 0 ? rowIdx : rowEnds.length;
    rowEnds[assignedRow] = nodeEnd;
    return { ...node, row: assignedRow };
  });
}

// ── GanttCanvas ───────────────────────────────────────────────────────────────

function GanttCanvas({ nodes, selectedNodeId, onSelectNode, isReadOnly, showChips = false, context, phases = [], onRemoveDependency, canvasRef, releases = [], onClickRelease, roadmapBands = [] }) {
  const effectiveRowH = showChips ? ROW_H + 16 : ROW_H;
  const { startMs, endMs } = useMemo(() => getTimeRange(nodes), [nodes]);
  const totalMs = endMs - startMs;
  const months = useMemo(() => getMonthHeaders(startMs, endMs), [startMs, endMs]);

  const appMap = useMemo(
    () => new Map((context?.applications || []).map((a) => [a.id, a.name])),
    [context]
  );

  const maxRow = nodes.reduce((m, n) => Math.max(m, n.row || 0), 0);
  const canvasHeight = Math.max((maxRow + 1) * effectiveRowH, 80);
  const [hoveredArrowId, setHoveredArrowId] = useState(null);

  const todayMs = new Date().getTime();
  const todayPct = ((todayMs - startMs) / totalMs) * 100;
  const showToday = todayPct > 0 && todayPct < 100;

  return (
    <Box ref={canvasRef} sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      {/* Month header */}
      <Box sx={{ display: 'flex', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', minWidth: 0 }}>
        {months.map((m, i) => (
          <Box
            key={i}
            sx={{
              flex: `0 0 ${m.widthPct}%`,
              minWidth: `${m.widthPct}%`,
              px: 0.5,
              py: 0.5,
              fontSize: '0.68rem',
              color: 'text.secondary',
              borderRight: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
              boxSizing: 'border-box',
              userSelect: 'none',
            }}
          >
            {m.label}
          </Box>
        ))}
      </Box>

      {/* Canvas body */}
      <Box sx={{ position: 'relative', height: canvasHeight, minWidth: '100%' }}>
        {/* Grid lines */}
        {months.map((m, i) =>
          i > 0 ? (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                left: `${m.leftPct}%`,
                top: 0,
                bottom: 0,
                width: '1px',
                bgcolor: 'divider',
                pointerEvents: 'none',
              }}
            />
          ) : null
        )}

        {/* Roadmap bands — lowest z-index, behind phase bands */}
        {roadmapBands.map((band, i) => {
          const bandTop = (band.from_row || 0) * effectiveRowH;
          const bandH   = Math.max(1, (band.to_row || 0) - (band.from_row || 0) + 1) * effectiveRowH;
          return (
            <Box key={band.name + i} sx={{
              position: 'absolute', left: 0, right: 0, top: bandTop, height: bandH,
              bgcolor: band.color + '10',
              zIndex: -1, pointerEvents: 'none',
            }}>
              <Typography sx={{
                position: 'absolute', bottom: 3, left: 6,
                fontSize: '0.58rem', fontWeight: 700, color: band.color,
                opacity: 0.7, letterSpacing: 0.75, textTransform: 'uppercase',
                pointerEvents: 'none', lineHeight: 1,
              }}>
                {band.name}
              </Typography>
            </Box>
          );
        })}

        {/* Phase bands */}
        {phases.map((ph, i) => {
          const bandTop = (ph.from_row || 0) * effectiveRowH;
          const bandH = Math.max(1, (ph.to_row || 0) - (ph.from_row || 0) + 1) * effectiveRowH;
          const phStartMs = ph.start_date ? toMs(ph.start_date) : startMs;
          const phEndMs   = ph.end_date   ? toMs(ph.end_date)   : endMs;
          const phLeftPct  = Math.max(0, ((phStartMs - startMs) / totalMs) * 100);
          const phRightPct = Math.min(100, ((phEndMs  - startMs) / totalMs) * 100);
          const phWidthPct = Math.max(0, phRightPct - phLeftPct);
          return (
            <Box key={ph.id || i} sx={{
              position: 'absolute',
              left: `${phLeftPct}%`,
              width: `${phWidthPct}%`,
              top: bandTop,
              height: bandH,
              bgcolor: ph.color + '18',
              borderTop: `2px solid ${ph.color}55`,
              borderBottom: `1px solid ${ph.color}33`,
              borderLeft: ph.start_date ? `2px solid ${ph.color}88` : 'none',
              borderRight: ph.end_date  ? `2px solid ${ph.color}88` : 'none',
              zIndex: 0,
              pointerEvents: 'none',
            }}>
              {ph.name && (
                <Typography sx={{
                  position: 'absolute',
                  top: 3, right: 6,
                  fontSize: '0.58rem',
                  fontWeight: 700,
                  color: ph.color,
                  opacity: 0.9,
                  letterSpacing: 0.75,
                  textTransform: 'uppercase',
                  pointerEvents: 'none',
                  lineHeight: 1,
                }}>
                  {ph.name}
                </Typography>
              )}
            </Box>
          );
        })}

        {/* Release markers */}
        {releases.map((rel) => {
          const relMs = toMs(rel.date);
          if (!rel.date || relMs < startMs || relMs > endMs) return null;
          const leftPct = ((relMs - startMs) / totalMs) * 100;
          return (
            <Tooltip key={rel.id} title={rel.notes ? `${rel.label}: ${rel.notes}` : rel.label} placement="top">
              <Box
                onClick={() => onClickRelease?.(rel)}
                sx={{
                  position: 'absolute',
                  left: `${leftPct}%`,
                  top: 0,
                  bottom: 0,
                  width: 0,
                  borderLeft: `2px dashed ${rel.color}`,
                  zIndex: 3,
                  cursor: onClickRelease ? 'pointer' : 'default',
                  '&:hover': onClickRelease ? { borderLeftStyle: 'solid' } : {},
                }}
              >
                {/* Diamond marker at top */}
                <Box sx={{
                  position: 'absolute',
                  top: -1,
                  left: -7,
                  width: 12,
                  height: 12,
                  bgcolor: rel.color,
                  transform: 'rotate(45deg)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                }} />
                {/* Label */}
                <Typography sx={{
                  position: 'absolute',
                  top: 14,
                  left: 6,
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  color: rel.color,
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  letterSpacing: 0.3,
                  textShadow: '0 1px 2px rgba(255,255,255,0.9)',
                }}>
                  {rel.label}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}

        {/* Row alternating background */}
        {Array.from({ length: maxRow + 1 }).map((_, row) => (
          <Box
            key={row}
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: row * effectiveRowH,
              height: effectiveRowH,
              bgcolor: row % 2 === 0 ? 'transparent' : 'action.hover',
              opacity: 0.4,
              pointerEvents: 'none',
            }}
          />
        ))}

        {/* Today line */}
        {showToday && (
          <Box
            sx={{
              position: 'absolute',
              left: `${todayPct}%`,
              top: 0,
              bottom: 0,
              width: '2px',
              bgcolor: 'error.main',
              opacity: 0.6,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Bars */}
        {nodes.map((node) => {
          const nodeStartMs = toMs(node.start_date);
          const nodeEndMs = toMs(node.end_date);
          if (nodeStartMs >= endMs || nodeEndMs <= startMs) return null;

          const leftPct = Math.max(0, ((nodeStartMs - startMs) / totalMs) * 100);
          const rightPct = Math.min(100, ((nodeEndMs - startMs) / totalMs) * 100);
          const widthPct = Math.max(0.3, rightPct - leftPct);
          const top = (node.row || 0) * effectiveRowH + 4;
          const isSelected = node.id === selectedNodeId;

          return (
            <Tooltip
              key={node.id}
              title={`${node.name} · ${formatDisplayDate(node.start_date)} – ${formatDisplayDate(node.end_date)}`}
              placement="top"
            >
              <Box
                onClick={(e) => { e.stopPropagation(); onSelectNode(node.id); }}
                sx={{
                  position: 'absolute',
                  left: `${leftPct}%`,
                  width: `${widthPct}%`,
                  top,
                  height: effectiveRowH - 8,
                  borderRadius: '4px',
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                  border: isSelected ? '2px solid #fff' : '2px solid transparent',
                  boxShadow: isSelected
                    ? '0 0 0 2px #1976d2, 0 2px 6px rgba(0,0,0,0.3)'
                    : '0 1px 3px rgba(0,0,0,0.2)',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  pl: node.status && node.status !== 'Not Started' ? 2 : 0.75,
                  pr: 0.75,
                  overflow: 'hidden',
                  zIndex: 2,
                  transition: 'box-shadow 0.1s',
                  '&:hover': { filter: 'brightness(1.1)' },
                }}
              >
                {/* Progress fill layers — rendered as DOM children so html2canvas captures them */}
                {(() => {
                  const color = node.color || '#42a5f5';
                  const pct = (node.status === 'Complete') ? 100 : Math.min(100, Math.max(0, node.progress ?? 0));
                  return (
                    <>
                      <Box sx={{
                        position: 'absolute', left: 0, top: 0, bottom: 0,
                        width: `${pct}%`, bgcolor: color, pointerEvents: 'none',
                      }} />
                      {pct < 100 && (
                        <Box sx={{
                          position: 'absolute', left: `${pct}%`, top: 0, bottom: 0, right: 0,
                          bgcolor: `${color}33`,
                          pointerEvents: 'none', overflow: 'hidden',
                        }}>
                          {/* Inline SVG diagonal lines — html2canvas captures SVG DOM nodes reliably */}
                          <svg
                            width="100%"
                            height="100%"
                            style={{ position: 'absolute', top: 0, left: 0 }}
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            {Array.from({ length: 60 }, (_, i) => (
                              <line
                                key={i}
                                x1={(i - 5) * 8}
                                y1={0}
                                x2={(i - 5) * 8 + 50}
                                y2={50}
                                stroke={color}
                                strokeWidth="2.5"
                                strokeOpacity="0.45"
                              />
                            ))}
                          </svg>
                        </Box>
                      )}
                    </>
                  );
                })()}
                {node.status && node.status !== 'Not Started' && (
                  <Box sx={{
                    position: 'absolute',
                    left: 5,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    bgcolor: STATUS_COLORS[node.status] || '#9e9e9e',
                    border: '1.5px solid rgba(255,255,255,0.7)',
                    pointerEvents: 'none',
                    flexShrink: 0,
                    zIndex: 1,
                  }} />
                )}
                <Typography
                  noWrap
                  sx={{
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: '#fff',
                    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                    lineHeight: 1,
                    pointerEvents: 'none',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {node.name}
                </Typography>
                {(() => {
                  const outcomeLabel = node.short_label ||
                    (node.outcome_id
                      ? (context?.outcomes?.find((o) => o.id === node.outcome_id)?.name || '').slice(0, 20)
                      : '');
                  return outcomeLabel ? (
                    <Typography
                      noWrap
                      sx={{
                        fontSize: '0.55rem',
                        color: 'rgba(255,255,255,0.75)',
                        lineHeight: 1,
                        pointerEvents: 'none',
                        position: 'relative',
                        zIndex: 1,
                        letterSpacing: 0.2,
                        mt: 0.25,
                      }}
                    >
                      {outcomeLabel}
                    </Typography>
                  ) : null;
                })()}
                {showChips && node.app_links?.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'nowrap', overflow: 'hidden', mt: 0.25 }}>
                    {node.app_links.slice(0, 3).map((id) => (
                      <Chip
                        key={id}
                        label={appMap.get(id) || id}
                        size="small"
                        sx={{
                          height: 12,
                          fontSize: '0.5rem',
                          bgcolor: 'rgba(255,255,255,0.25)',
                          color: '#fff',
                          pointerEvents: 'none',
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                    ))}
                    {node.app_links.length > 3 && (
                      <Typography sx={{ fontSize: '0.5rem', color: '#fff', opacity: 0.8, alignSelf: 'center', pointerEvents: 'none' }}>
                        +{node.app_links.length - 3}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Tooltip>
          );
        })}

        {/* Dependency arrows */}
        {(() => {
          const nodeMap = new Map(nodes.map((n) => [n.id, n]));
          const releaseMap = new Map(releases.map((r) => [r.id, r]));
          const arrows = [];
          nodes.forEach((node) => {
            if (!node.depends_on?.length) return;
            const x2Pct = Math.max(0, ((toMs(node.start_date) - startMs) / totalMs) * 100);
            node.depends_on.forEach((predId) => {
              const pred = nodeMap.get(predId);
              const predRelease = !pred ? releaseMap.get(predId) : null;
              if (!pred && !predRelease) return;

              const x1Pct = pred
                ? Math.min(100, ((toMs(pred.end_date) - startMs) / totalMs) * 100)
                : Math.min(100, ((toMs(predRelease.date) - startMs) / totalMs) * 100);
              const y1 = pred
                ? (pred.row || 0) * effectiveRowH + effectiveRowH / 2
                : canvasHeight / 2;
              const y2 = (node.row || 0) * effectiveRowH + effectiveRowH / 2;
              arrows.push({ id: `${predId}::${node.id}`, predId, nodeId: node.id, x1Pct, y1, x2Pct, y2, isReleaseDep: !!predRelease });
            });
          });
          if (!arrows.length) return null;
          const interactive = !!onRemoveDependency && !isReadOnly;
          return (
            <svg
              style={{
                position: 'absolute', top: 0, left: 0,
                width: '100%', height: canvasHeight,
                pointerEvents: interactive ? 'auto' : 'none', zIndex: 4, overflow: 'visible',
              }}
            >
              <defs>
                <marker id="dep-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
                  <path d="M0,0 L0,6 L6,3 z" fill="#ffa726" />
                </marker>
                <marker id="dep-arrow-hover" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
                  <path d="M0,0 L0,6 L6,3 z" fill="#ff6d00" />
                </marker>
                <marker id="rel-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
                  <path d="M0,0 L0,6 L6,3 z" fill="#ab47bc" />
                </marker>
                <marker id="rel-arrow-hover" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto" markerUnits="strokeWidth">
                  <path d="M0,0 L0,6 L6,3 z" fill="#7b1fa2" />
                </marker>
              </defs>
              {arrows.map(({ id, predId, nodeId, x1Pct, y1, x2Pct, y2, isReleaseDep }) => {
                const hovered = hoveredArrowId === id;
                const canRemove = interactive && !isReleaseDep;
                const strokeColor = isReleaseDep
                  ? (hovered ? '#7b1fa2' : '#ab47bc')
                  : (hovered ? '#ff6d00' : '#ffa726');
                const markerEndId = isReleaseDep
                  ? (hovered ? 'url(#rel-arrow-hover)' : 'url(#rel-arrow)')
                  : (hovered ? 'url(#dep-arrow-hover)' : 'url(#dep-arrow)');
                return (
                  <g
                    key={id}
                    style={{ cursor: canRemove ? 'pointer' : 'default' }}
                    onMouseEnter={() => setHoveredArrowId(id)}
                    onMouseLeave={() => setHoveredArrowId(null)}
                    onClick={() => canRemove && onRemoveDependency(predId, nodeId)}
                  >
                    <line
                      x1={`${x1Pct}%`} y1={y1}
                      x2={`${x2Pct}%`} y2={y2}
                      stroke="transparent" strokeWidth="12"
                    />
                    <line
                      x1={`${x1Pct}%`} y1={y1}
                      x2={`${x2Pct}%`} y2={y2}
                      stroke={strokeColor}
                      strokeWidth={hovered ? 2.5 : 1.5}
                      strokeDasharray={isReleaseDep ? '5,3' : undefined}
                      opacity={hovered ? 1 : 0.8}
                      markerEnd={markerEndId}
                    />
                  </g>
                );
              })}
            </svg>
          );
        })()}

        {nodes.length === 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="caption" color="text.disabled">
              {isReadOnly ? 'No items.' : 'No items — use Add → Item to get started.'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// ── NodePanel ─────────────────────────────────────────────────────────────────

function NodePanel({ node, onUpdate, onDelete, onClose, isReadOnly, context, dmNodes = [], allNodes = [], roster = [], releases = [] }) {
  const [local, setLocal] = useState(node);
  const [syncing, setSyncing] = useState(false);

  // Sync if parent swaps to a different node
  React.useEffect(() => { setLocal(node); }, [node]);

  const set = (field, value) => {
    const updated = { ...local, [field]: value };
    setLocal(updated);
    onUpdate(updated);
  };

  const handleMilestoneSync = async () => {
    if (!local.milestone_id || !local.outcome_id) return;
    setSyncing(true);
    try {
      const res = await fetch(`${API_BASE}/api/outcomes/${local.outcome_id}`);
      if (!res.ok) throw new Error();
      const outcome = await res.json();
      const milestone = (outcome.milestones || []).find((m) => m.id === local.milestone_id);
      if (milestone) {
        const updated = {
          ...local,
          name: milestone.name || local.name,
          start_date: milestone.start_date || local.start_date,
          end_date: milestone.end_date || local.end_date,
        };
        setLocal(updated);
        onUpdate(updated);
      }
    } catch {
      // ignore
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box
      sx={{
        width: 280,
        flexShrink: 0,
        borderLeft: '1px solid',
        borderColor: 'divider',
        pl: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        overflow: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          Edit Item
        </Typography>
        {!isReadOnly && (
          <Tooltip title="Delete item">
            <IconButton size="small" color="error" onClick={() => onDelete(node.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TextField
          label="Name"
          value={local.name}
          onChange={(e) => set('name', e.target.value)}
          size="small"
          fullWidth
          InputProps={{ readOnly: isReadOnly }}
        />
        {local.milestone_id && !isReadOnly && (
          <Tooltip title="Sync name and dates from source milestone">
            <span>
              <IconButton size="small" onClick={handleMilestoneSync} disabled={syncing}>
                {syncing ? <CircularProgress size={14} /> : <SyncIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      <TextField
        label="Short label (shown on bar)"
        value={local.short_label || ''}
        onChange={(e) => set('short_label', e.target.value)}
        size="small"
        fullWidth
        placeholder="Auto from outcome name…"
        InputProps={{ readOnly: isReadOnly }}
      />

      {/* Status */}
      <Box>
        <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
          Status
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {STATUSES.map((s) => (
            <Chip
              key={s}
              label={s}
              size="small"
              onClick={() => !isReadOnly && set('status', s)}
              sx={{
                fontSize: '0.65rem',
                bgcolor: local.status === s ? STATUS_COLORS[s] : 'transparent',
                color: local.status === s ? '#fff' : STATUS_COLORS[s],
                border: `1.5px solid ${STATUS_COLORS[s]}`,
                cursor: isReadOnly ? 'default' : 'pointer',
                fontWeight: local.status === s ? 700 : 400,
                '&:hover': { opacity: isReadOnly ? 1 : 0.85 },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Progress */}
      <Box>
        <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
          Progress: {local.progress ?? 0}%
        </Typography>
        <Slider
          value={local.progress ?? 0}
          onChange={(_, v) => set('progress', v)}
          min={0}
          max={100}
          step={5}
          disabled={isReadOnly}
          size="small"
          sx={{ color: local.color || '#42a5f5', pt: 0.5 }}
        />
      </Box>

      {/* Owner */}
      {roster.length > 0 && (
        <Box>
          <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
            Owner
          </Typography>
          <Autocomplete
            size="small"
            options={roster.map((r) => r.id)}
            getOptionLabel={(id) => roster.find((r) => r.id === id)?.name || id}
            value={local.owner_id || null}
            onChange={(_, v) => set('owner_id', v || '')}
            disabled={isReadOnly}
            renderInput={(params) => <TextField {...params} placeholder="Assign owner…" />}
          />
        </Box>
      )}

      <TextField
        label="Start Date"
        type="date"
        value={local.start_date}
        onChange={(e) => set('start_date', e.target.value)}
        size="small"
        fullWidth
        InputLabelProps={{ shrink: true }}
        InputProps={{ readOnly: isReadOnly }}
      />

      <TextField
        label="End Date"
        type="date"
        value={local.end_date}
        onChange={(e) => set('end_date', e.target.value)}
        size="small"
        fullWidth
        InputLabelProps={{ shrink: true }}
        InputProps={{ readOnly: isReadOnly }}
      />

      {/* Row with move up/down */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
          Row: <strong>{local.row}</strong>
        </Typography>
        {!isReadOnly && (
          <>
            <Tooltip title="Move up (lower row)">
              <span>
                <IconButton
                  size="small"
                  onClick={() => set('row', Math.max(0, local.row - 1))}
                  disabled={local.row === 0}
                >
                  <MoveUpIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move down (higher row)">
              <IconButton size="small" onClick={() => set('row', local.row + 1)}>
                <MoveDownIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Color
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {NODE_COLORS.map((c) => (
            <Box
              key={c}
              onClick={() => !isReadOnly && set('color', c)}
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                bgcolor: c,
                cursor: isReadOnly ? 'default' : 'pointer',
                border: local.color === c ? '2px solid #fff' : '2px solid transparent',
                boxShadow: local.color === c ? `0 0 0 2px ${c}` : 'none',
              }}
            />
          ))}
        </Box>
      </Box>

      <TextField
        label="Notes"
        value={local.notes}
        onChange={(e) => set('notes', e.target.value)}
        size="small"
        fullWidth
        multiline
        rows={3}
        InputProps={{ readOnly: isReadOnly }}
      />

      {/* Dependencies */}
      {(allNodes.length > 1 || releases.length > 0) && (
        <Box>
          <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
            Dependencies (must finish first)
          </Typography>
          <Autocomplete
            multiple
            size="small"
            options={[
              ...allNodes.filter((n) => n.id !== local.id).map((n) => n.id),
              ...releases.map((r) => r.id),
            ]}
            groupBy={(id) => id.startsWith('rel-') ? 'Releases' : 'Items'}
            getOptionLabel={(id) => {
              if (id.startsWith('rel-')) {
                const r = releases.find((r) => r.id === id);
                return r ? `${r.label} · ${r.date}` : id;
              }
              const n = allNodes.find((n) => n.id === id);
              return n ? `${n.name} (ends ${formatDisplayDate(n.end_date)})` : id;
            }}
            value={local.depends_on || []}
            onChange={(_, v) => set('depends_on', v)}
            disabled={isReadOnly}
            renderInput={(params) => <TextField {...params} placeholder="Search items and releases…" />}
            renderTags={(val, getTagProps) =>
              val.map((id, i) => {
                const isRel = id.startsWith('rel-');
                const label = isRel
                  ? (releases.find((r) => r.id === id)?.label ?? id)
                  : (allNodes.find((n) => n.id === id)?.name ?? id);
                return (
                  <Chip
                    {...getTagProps({ index: i })}
                    key={id}
                    label={label}
                    size="small"
                    sx={{ bgcolor: isRel ? '#ab47bc22' : '#ffa72622', fontSize: '0.65rem' }}
                  />
                );
              })
            }
          />
        </Box>
      )}

      {/* Outcome links */}
      <Box>
        <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
          Outcomes
        </Typography>
        <Autocomplete
          multiple
          size="small"
          options={(context?.outcomes || []).map((o) => o.id)}
          getOptionLabel={(id) => context?.outcomes?.find((o) => o.id === id)?.name || id}
          value={local.outcome_links || []}
          onChange={(_, v) => set('outcome_links', v)}
          disabled={isReadOnly}
          renderOption={(props, id) => {
            const o = context?.outcomes?.find((o) => o.id === id);
            const budget = o ? formatBudget(o.approved_budget) : null;
            return (
              <li {...props} key={id}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.1 }}>
                  <Typography variant="body2">{o?.name || id}</Typography>
                  {(o?.status || budget) && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                      {[o?.status, budget].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </Box>
              </li>
            );
          }}
          renderInput={(params) => <TextField {...params} placeholder="Search outcomes…" />}
          renderTags={(val, getTagProps) =>
            val.map((id, i) => (
              <Chip
                {...getTagProps({ index: i })}
                key={id}
                label={context?.outcomes?.find((o) => o.id === id)?.name || id}
                size="small"
                sx={{ bgcolor: '#42a5f522', fontSize: '0.65rem' }}
              />
            ))
          }
        />
      </Box>

      {/* Application links */}
      <Box>
        <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
          Applications
        </Typography>
        <Autocomplete
          multiple
          size="small"
          options={(context?.applications || []).map((a) => a.id)}
          getOptionLabel={(id) => context?.applications?.find((a) => a.id === id)?.name || id}
          value={local.app_links || []}
          onChange={(_, v) => set('app_links', v)}
          disabled={isReadOnly}
          renderInput={(params) => <TextField {...params} placeholder="Search applications…" />}
          renderTags={(val, getTagProps) =>
            val.map((id, i) => (
              <Chip
                {...getTagProps({ index: i })}
                key={id}
                label={context?.applications?.find((a) => a.id === id)?.name || id}
                size="small"
                sx={{ bgcolor: '#ffa72622', fontSize: '0.65rem' }}
              />
            ))
          }
        />
      </Box>

      {/* Data Map Node links */}
      {dmNodes.length > 0 && (
        <Box>
          <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
            Data Map Nodes
          </Typography>
          <Autocomplete
            multiple
            size="small"
            options={dmNodes.map((n) => n.id)}
            getOptionLabel={(id) => {
              const n = dmNodes.find((n) => n.id === id);
              return n ? `${n.label} (${n.map_name})` : id;
            }}
            value={local.data_map_links || []}
            onChange={(_, v) => set('data_map_links', v)}
            disabled={isReadOnly}
            renderInput={(params) => <TextField {...params} placeholder="Search data map nodes…" />}
            renderTags={(val, getTagProps) =>
              val.map((id, i) => {
                const n = dmNodes.find((n) => n.id === id);
                return (
                  <Chip
                    {...getTagProps({ index: i })}
                    key={id}
                    label={n ? n.label : id}
                    size="small"
                    sx={{ bgcolor: '#66bb6a22', fontSize: '0.65rem' }}
                  />
                );
              })
            }
          />
        </Box>
      )}
    </Box>
  );
}

// ── PhasesPanel ───────────────────────────────────────────────────────────────

function PhasesPanel({ phases, onChange, maxRow }) {
  const updatePhase = (idx, patch) => {
    const next = phases.map((p, i) => i === idx ? { ...p, ...patch } : p);
    onChange(next);
  };

  const removePhase = (idx) => onChange(phases.filter((_, i) => i !== idx));

  const addPhase = () => {
    const lastTo = phases.reduce((m, p) => Math.max(m, p.to_row || 0), -1);
    onChange([...phases, newPhase(Math.max(0, lastTo + 1))]);
  };

  return (
    <Box sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      p: 1.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" fontWeight={700}>Phases / Swimlanes</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addPhase} sx={{ fontSize: '0.65rem', py: 0.25 }}>
          Add Phase
        </Button>
      </Box>

      {phases.length === 0 && (
        <Typography variant="caption" color="text.disabled">
          No phases. Add one to group Gantt rows into named swimlanes.
        </Typography>
      )}

      {phases.map((ph, idx) => (
        <Box key={ph.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          {/* Color picker */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {PHASE_COLORS.map((c) => (
              <Box
                key={c}
                onClick={() => updatePhase(idx, { color: c })}
                sx={{
                  width: 14, height: 14, borderRadius: '50%', bgcolor: c,
                  cursor: 'pointer',
                  border: ph.color === c ? '2px solid #fff' : '2px solid transparent',
                  boxShadow: ph.color === c ? `0 0 0 1.5px ${c}` : 'none',
                  flexShrink: 0,
                }}
              />
            ))}
          </Box>
          <TextField
            size="small"
            value={ph.name}
            onChange={(e) => updatePhase(idx, { name: e.target.value })}
            placeholder="Phase name"
            sx={{ width: 130 }}
            inputProps={{ style: { fontSize: '0.72rem', padding: '3px 6px' } }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>Rows</Typography>
          <TextField
            size="small"
            type="number"
            value={ph.from_row}
            onChange={(e) => updatePhase(idx, { from_row: Math.max(0, parseInt(e.target.value) || 0) })}
            sx={{ width: 52 }}
            inputProps={{ min: 0, max: maxRow, style: { fontSize: '0.72rem', padding: '3px 6px' } }}
          />
          <Typography variant="caption" color="text.secondary">–</Typography>
          <TextField
            size="small"
            type="number"
            value={ph.to_row}
            onChange={(e) => updatePhase(idx, { to_row: Math.max(ph.from_row, parseInt(e.target.value) || 0) })}
            sx={{ width: 52 }}
            inputProps={{ min: ph.from_row, max: maxRow, style: { fontSize: '0.72rem', padding: '3px 6px' } }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>Dates</Typography>
          <TextField
            size="small"
            type="date"
            value={ph.start_date || ''}
            onChange={(e) => updatePhase(idx, { start_date: e.target.value })}
            sx={{ width: 130 }}
            inputProps={{ style: { fontSize: '0.72rem', padding: '3px 6px' } }}
            InputLabelProps={{ shrink: true }}
            placeholder="Start"
          />
          <Typography variant="caption" color="text.secondary">–</Typography>
          <TextField
            size="small"
            type="date"
            value={ph.end_date || ''}
            onChange={(e) => updatePhase(idx, { end_date: e.target.value })}
            sx={{ width: 130 }}
            inputProps={{ style: { fontSize: '0.72rem', padding: '3px 6px' } }}
            InputLabelProps={{ shrink: true }}
            placeholder="End"
          />
          <IconButton size="small" color="error" onClick={() => removePhase(idx)} sx={{ p: 0.25 }}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
}

// ── ImportMilestonesDialog ─────────────────────────────────────────────────────

function ImportMilestonesDialog({ open, onClose, onImport }) {
  const [outcomes, setOutcomes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setFilterText('');
    fetch(`${API_BASE}/api/outcomes`)
      .then((r) => r.json())
      .then((data) => {
        const list = (data.outcomes || []).filter((o) => !o.deleted);
        setOutcomes(list);
        setSelected(new Set());
      })
      .catch(() => setOutcomes([]))
      .finally(() => setLoading(false));
  }, [open]);

  // Build groups: each outcome → its importable items
  const groups = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    return outcomes
      .map((o) => {
        const milestones = (o.milestones || []).filter((m) => m.start_date && m.end_date);
        let items;
        if (milestones.length > 0) {
          items = milestones
            .filter((m) => !q || o.name.toLowerCase().includes(q) || m.name.toLowerCase().includes(q))
            .map((m) => ({
              key: `${o.id}::${m.id}`,
              name: m.name,
              start_date: m.start_date,
              end_date: m.end_date,
              outcome_id: o.id,
              outcome_name: o.name,
              milestone_id: m.id,
              app_links: o.applications || [],
            }));
        } else if (o.start_date && o.end_date) {
          if (!q || o.name.toLowerCase().includes(q)) {
            items = [{
              key: o.id,
              name: o.name,
              start_date: o.start_date,
              end_date: o.end_date,
              outcome_id: o.id,
              outcome_name: o.name,
              milestone_id: null,
              app_links: o.applications || [],
            }];
          } else {
            items = [];
          }
        } else {
          items = [];
        }
        return { outcome: o, items };
      })
      .filter((g) => g.items.length > 0);
  }, [outcomes, filterText]);

  const allItems = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  const toggle = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const toggleGroup = (groupItems) => {
    const keys = groupItems.map((it) => it.key);
    const allChecked = keys.every((k) => selected.has(k));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allChecked) {
        keys.forEach((k) => next.delete(k));
      } else {
        keys.forEach((k) => next.add(k));
      }
      return next;
    });
  };

  const handleImport = () => {
    const toImport = allItems.filter((it) => selected.has(it.key));
    const nodes = toImport.map((it, i) => ({
      ...newNode(i),
      name: it.name,
      start_date: it.start_date,
      end_date: it.end_date,
      outcome_id: it.outcome_id,
      milestone_id: it.milestone_id,
      outcome_links: [it.outcome_id],
      app_links: it.app_links,
      color: NODE_COLORS[i % NODE_COLORS.length],
    }));
    onImport(nodes);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import from Outcomes</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
        <TextField
          size="small"
          placeholder="Search outcomes or milestones…"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16 }} />
              </InputAdornment>
            ),
          }}
          fullWidth
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {filterText ? 'No matches found.' : 'No outcomes with date ranges found.'}
          </Typography>
        ) : (
          <Box>
            {groups.map(({ outcome, items }) => {
              const groupKeys = items.map((it) => it.key);
              const checkedCount = groupKeys.filter((k) => selected.has(k)).length;
              const allChecked = checkedCount === groupKeys.length;
              const someChecked = checkedCount > 0 && !allChecked;

              return (
                <Box key={outcome.id} sx={{ mb: 1.5 }}>
                  {/* Outcome header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    <Checkbox
                      size="small"
                      checked={allChecked}
                      indeterminate={someChecked}
                      onChange={() => toggleGroup(items)}
                      sx={{ p: 0.25 }}
                    />
                    <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1 }}>
                      {outcome.name}
                    </Typography>
                    {outcome.status && (
                      <Chip
                        label={outcome.status}
                        size="small"
                        sx={{ height: 16, fontSize: '0.6rem' }}
                        variant="outlined"
                      />
                    )}
                  </Box>

                  {/* Milestone items (indented) */}
                  <Box sx={{ pl: 3 }}>
                    {items.map((it) => (
                      <FormControlLabel
                        key={it.key}
                        sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.25 }}
                        control={
                          <Checkbox
                            checked={selected.has(it.key)}
                            onChange={() => toggle(it.key)}
                            size="small"
                            sx={{ pt: 0.25 }}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2">{it.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDisplayDate(it.start_date)} – {formatDisplayDate(it.end_date)}
                            </Typography>
                          </Box>
                        }
                      />
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={selected.size === 0}
          startIcon={<ImportIcon />}
        >
          Import {selected.size > 0 ? `(${selected.size})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── AggregateRoadmapView ──────────────────────────────────────────────────────

function AggregateRoadmapView({ onBack }) {
  const [roadmaps, setRoadmaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState(null); // null = all

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE}/api/roadmaps/`).then((r) => r.json()),
    ])
      .then(([list]) => {
        return Promise.all(
          list.map((rm) =>
            fetch(`${API_BASE}/api/roadmaps/${rm.id}`)
              .then((r) => r.json())
              .catch(() => null)
          )
        );
      })
      .then((full) => setRoadmaps(full.filter(Boolean)))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  // Initialise selectedIds to all after first load
  useEffect(() => {
    if (roadmaps.length > 0 && selectedIds === null)
      setSelectedIds(roadmaps.map((rm) => rm.id));
  }, [roadmaps, selectedIds]);

  const visibleRoadmaps = useMemo(
    () => (selectedIds ? roadmaps.filter((rm) => selectedIds.includes(rm.id)) : roadmaps),
    [roadmaps, selectedIds]
  );

  // Merge nodes, phases, releases, and build roadmap bands — all with row offsets applied
  const { allNodes, allPhases, allReleases, roadmapBands } = useMemo(() => {
    const allNodes = [], allPhases = [], allReleases = [], roadmapBands = [];
    let rowOffset = 0;
    visibleRoadmaps.forEach((rm, idx) => {
      const color   = ROADMAP_COLORS[idx % ROADMAP_COLORS.length];
      const rmNodes = rm.nodes || [];
      const maxRow  = rmNodes.reduce((m, n) => Math.max(m, n.row || 0), -1);
      const startRow = rowOffset;

      rmNodes.forEach((n) => allNodes.push({ ...n, row: (n.row || 0) + rowOffset, _roadmapName: rm.name }));
      (rm.phases || []).forEach((ph) => allPhases.push({
        ...ph,
        from_row: (ph.from_row || 0) + rowOffset,
        to_row:   (ph.to_row   || 0) + rowOffset,
      }));
      (rm.releases || []).forEach((r) => allReleases.push(r));
      roadmapBands.push({ name: rm.name, color, from_row: startRow, to_row: rowOffset + Math.max(maxRow, 0) });
      rowOffset += maxRow + 2;
    });
    return { allNodes, allPhases, allReleases, roadmapBands };
  }, [visibleRoadmaps]);

  const toggleRoadmap = (id) => {
    setSelectedIds((prev) => {
      const current = prev ?? roadmaps.map((rm) => rm.id);
      return current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <IconButton size="small" onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">All Roadmaps</Typography>
        {roadmaps.length > 0 && (
          <Chip label={`${roadmaps.length} roadmaps`} size="small" variant="outlined" />
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : allNodes.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No roadmap items to display.
        </Typography>
      ) : (
        <Box>
          {/* Roadmap toggle chips */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">Show:</Typography>
            {roadmaps.map((rm, idx) => {
              const color  = ROADMAP_COLORS[idx % ROADMAP_COLORS.length];
              const active = (selectedIds ?? roadmaps.map((r) => r.id)).includes(rm.id);
              return (
                <Chip
                  key={rm.id}
                  label={rm.name}
                  size="small"
                  onClick={() => toggleRoadmap(rm.id)}
                  sx={{
                    fontSize: '0.65rem',
                    cursor: 'pointer',
                    bgcolor: active ? color + '33' : 'transparent',
                    color: active ? color : 'text.secondary',
                    border: `1.5px solid ${active ? color : 'divider'}`,
                    fontWeight: active ? 700 : 400,
                    '&:hover': { opacity: 0.85 },
                  }}
                />
              );
            })}
          </Box>
          <GanttCanvas
            nodes={allNodes}
            selectedNodeId={null}
            onSelectNode={() => {}}
            isReadOnly
            phases={allPhases}
            releases={allReleases}
            roadmapBands={roadmapBands}
          />
        </Box>
      )}
    </Box>
  );
}

// ── RoadmapEditor ─────────────────────────────────────────────────────────────

function RoadmapEditor({ roadmap: initialRoadmap, isReadOnly, onBack, onSaved, onDeleted, embedded = false }) {
  const [name, setName] = useState(initialRoadmap.name);
  const [description, setDescription] = useState(initialRoadmap.description || '');
  const [nodes, setNodes] = useState(initialRoadmap.nodes || []);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [error, setError] = useState('');
  const [context, setContext] = useState({ outcomes: [], applications: [], functions: [] });
  const [showChips, setShowChips] = useState(false);
  const [dmNodes, setDmNodes] = useState([]);
  const [roster, setRoster] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [phases, setPhases] = useState(initialRoadmap.phases || []);
  const [releases, setReleases] = useState(initialRoadmap.releases || []);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);

  // Re-sync state when the parent passes a newer version of this roadmap (e.g. saved from another context)
  const lastSyncRef = useRef(initialRoadmap.updated_at);
  useEffect(() => {
    if (initialRoadmap.updated_at !== lastSyncRef.current) {
      lastSyncRef.current = initialRoadmap.updated_at;
      setName(initialRoadmap.name);
      setDescription(initialRoadmap.description || '');
      setNodes(initialRoadmap.nodes || []);
      setPhases(initialRoadmap.phases || []);
      setReleases(initialRoadmap.releases || []);
    }
  }, [initialRoadmap]);

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/api/doc-hub/context`),
      fetch(`${API_BASE}/api/data-maps/nodes`),
      fetch(`${API_BASE}/api/it-roster`),
    ]).then(([ctxRes, dmRes, rosterRes]) => {
      if (ctxRes.ok) ctxRes.json().then(setContext);
      if (dmRes.ok) dmRes.json().then(setDmNodes);
      if (rosterRes.ok) rosterRes.json().then((data) => setRoster(Array.isArray(data) ? data : (data.members || [])));
    }).catch(() => {});
  }, []);

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId) || null,
    [nodes, selectedNodeId]
  );

  const handleAddNode = () => {
    const maxRow = nodes.reduce((m, n) => Math.max(m, n.row || 0), -1);
    const node = newNode(maxRow + 1);
    setNodes((prev) => [...prev, node]);
    setSelectedNodeId(node.id);
  };

  const handleAddPhase = () => {
    const maxTo = phases.reduce((m, p) => Math.max(m, p.to_row || 0), -1);
    setPhases((prev) => [...prev, newPhase(Math.max(0, maxTo + 1))]);
  };

  const handleUpdateNode = useCallback((updated) => {
    setNodes((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
  }, []);

  const handleDeleteNode = useCallback((nodeId) => {
    setNodes((prev) => prev.filter((n) => n.id !== nodeId));
    setSelectedNodeId(null);
  }, []);

  const handleRemoveDependency = useCallback((predId, nodeId) => {
    setNodes((prev) => prev.map((n) =>
      n.id === nodeId ? { ...n, depends_on: (n.depends_on || []).filter((id) => id !== predId) } : n
    ));
  }, []);

  const handleImport = useCallback((importedNodes) => {
    setNodes((prev) => {
      const maxRow = prev.reduce((m, n) => Math.max(m, n.row || 0), -1);
      const offset = maxRow + 1;
      return [
        ...prev,
        ...importedNodes.map((n, i) => ({ ...n, row: offset + i })),
      ];
    });
  }, []);

  const handleSave = async () => {
    if (!name.trim()) { setError('Roadmap name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/roadmaps/${initialRoadmap.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), description: description.trim(), nodes, phases, releases }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      const saved = await res.json();
      onSaved(saved);
      window.dispatchEvent(new CustomEvent('roadmap-updated', { detail: { roadmap: saved } }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete roadmap "${name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/api/roadmaps/${initialRoadmap.id}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) throw new Error(`Delete failed (${res.status})`);
      onDeleted(initialRoadmap.id);
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  const canvasRef = useRef(null);

  const handleExportImage = async () => {
    const wrapper = canvasRef.current;
    if (!wrapper) return;
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(wrapper, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        width: wrapper.scrollWidth,
        height: wrapper.scrollHeight,
        windowWidth: wrapper.scrollWidth,
        windowHeight: wrapper.scrollHeight,
      });
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `${(name || 'roadmap').replace(/[^a-z0-9_-]/gi, '_')}.png`;
      a.click();
    } catch {
      setError('Image export failed.');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        {!embedded && (
          <IconButton size="small" onClick={onBack}>
            <ArrowBackIcon />
          </IconButton>
        )}

        <TextField
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          placeholder="Roadmap name"
          InputProps={{ readOnly: isReadOnly, style: { fontWeight: 600 } }}
          sx={{ width: 220 }}
        />

        <TextField
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          size="small"
          placeholder="Description (optional)"
          InputProps={{ readOnly: isReadOnly }}
          sx={{ flex: 1, minWidth: 140 }}
        />

        {!isReadOnly && (
          <>
            {/* Add ▾ menu */}
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={(e) => setAddMenuAnchor(e.currentTarget)}
            >
              Add
            </Button>
            <Menu
              anchorEl={addMenuAnchor}
              open={Boolean(addMenuAnchor)}
              onClose={() => setAddMenuAnchor(null)}
            >
              <MenuItem onClick={() => { handleAddNode(); setAddMenuAnchor(null); }}>
                <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Item</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { setImportOpen(true); setAddMenuAnchor(null); }}>
                <ListItemIcon><ImportIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Outcome / Milestone</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { handleAddPhase(); setAddMenuAnchor(null); }}>
                <ListItemIcon><PhasesIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Phase</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { setEditingRelease(null); setReleaseDialogOpen(true); setAddMenuAnchor(null); }}>
                <ListItemIcon><EventIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Release</ListItemText>
              </MenuItem>
            </Menu>

            <Button
              size="small"
              variant="contained"
              startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving || deleting}
              sx={{ bgcolor: '#00897b', '&:hover': { bgcolor: '#00695c' } }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>

            {/* ⋯ overflow menu */}
            <IconButton size="small" onClick={(e) => setMoreMenuAnchor(e.currentTarget)}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={moreMenuAnchor}
              open={Boolean(moreMenuAnchor)}
              onClose={() => setMoreMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => { setNodes((prev) => autoArrange(prev)); setMoreMenuAnchor(null); }}
                disabled={nodes.length === 0}
              >
                <ListItemIcon><SortIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Auto-sort rows</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => { setShowChips((p) => !p); setMoreMenuAnchor(null); }}>
                <ListItemIcon>
                  <LabelIcon fontSize="small" sx={{ color: showChips ? 'primary.main' : 'inherit' }} />
                </ListItemIcon>
                <ListItemText>{showChips ? 'Hide app chips' : 'Show app chips'}</ListItemText>
              </MenuItem>
              {!embedded && <Divider />}
              {!embedded && (
                <MenuItem
                  onClick={() => { handleDelete(); setMoreMenuAnchor(null); }}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                  </ListItemIcon>
                  <ListItemText>Delete roadmap</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </>
        )}
        {nodes.length > 0 && (
          <Tooltip title="Export roadmap as PNG image">
            <IconButton size="small" onClick={handleExportImage}>
              <ImageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Phases panel — auto-shows when phases exist */}
      {phases.length > 0 && !isReadOnly && (
        <PhasesPanel
          phases={phases}
          onChange={setPhases}
          maxRow={nodes.reduce((m, n) => Math.max(m, n.row || 0), 0)}
        />
      )}

      {/* Status filter chips */}
      {nodes.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">Filter:</Typography>
          {STATUSES.map((s) => {
            const active = statusFilter.includes(s);
            return (
              <Chip
                key={s}
                label={s}
                size="small"
                onClick={() => setStatusFilter((prev) =>
                  prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
                )}
                sx={{
                  fontSize: '0.65rem',
                  bgcolor: active ? STATUS_COLORS[s] : 'transparent',
                  color: active ? '#fff' : STATUS_COLORS[s],
                  border: `1.5px solid ${STATUS_COLORS[s]}`,
                  cursor: 'pointer',
                  fontWeight: active ? 700 : 400,
                  '&:hover': { opacity: 0.85 },
                }}
              />
            );
          })}
          {statusFilter.length > 0 && (
            <Chip
              label="Clear"
              size="small"
              onClick={() => setStatusFilter([])}
              sx={{ fontSize: '0.65rem' }}
            />
          )}
        </Box>
      )}

      {/* Gantt + NodePanel */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <GanttCanvas
            nodes={statusFilter.length > 0 ? nodes.filter((n) => statusFilter.includes(n.status || 'Not Started')) : nodes}
            selectedNodeId={selectedNodeId}
            onSelectNode={setSelectedNodeId}
            isReadOnly={isReadOnly}
            showChips={showChips}
            context={context}
            phases={phases}
            onRemoveDependency={isReadOnly ? undefined : handleRemoveDependency}
            canvasRef={canvasRef}
            releases={releases}
            onClickRelease={isReadOnly ? undefined : (rel) => { setEditingRelease(rel); setReleaseDialogOpen(true); }}
          />
        </Box>

        {selectedNode && (
          <NodePanel
            node={selectedNode}
            onUpdate={handleUpdateNode}
            onDelete={handleDeleteNode}
            onClose={() => setSelectedNodeId(null)}
            isReadOnly={isReadOnly}
            context={context}
            dmNodes={dmNodes}
            allNodes={nodes}
            roster={roster}
            releases={releases}
          />
        )}
      </Box>

      <ImportMilestonesDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleImport}
      />

      <ReleaseEditDialog
        open={releaseDialogOpen}
        release={editingRelease}
        onSave={(rel) => {
          setReleases((prev) => {
            const exists = prev.find((r) => r.id === rel.id);
            return exists ? prev.map((r) => (r.id === rel.id ? rel : r)) : [...prev, rel];
          });
          setReleaseDialogOpen(false);
          setEditingRelease(null);
        }}
        onDelete={(relId) => {
          setReleases((prev) => prev.filter((r) => r.id !== relId));
          setReleaseDialogOpen(false);
          setEditingRelease(null);
        }}
        onClose={() => { setReleaseDialogOpen(false); setEditingRelease(null); }}
      />
    </Box>
  );
}

// ── Roadmap (outer container) ─────────────────────────────────────────────────

function Roadmap({ isReadOnly = false, openRoadmapId, onRoadmapOpened }) {
  // views: 'list' | 'editor' | 'aggregate'
  const [view, setView] = useState('list');
  const [roadmaps, setRoadmaps] = useState([]);
  const [selectedRoadmap, setSelectedRoadmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [searchRoadmaps, setSearchRoadmaps] = useState('');

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/roadmaps/`);
      if (!res.ok) throw new Error(`Failed to load roadmaps (${res.status})`);
      setRoadmaps(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Keep list + open editor in sync when any roadmap-updated event fires (e.g. saved from outcome tab)
  useEffect(() => {
    const handler = (e) => {
      const updated = e.detail?.roadmap;
      if (!updated) return;
      setRoadmaps((prev) => {
        const idx = prev.findIndex((r) => r.id === updated.id);
        if (idx < 0) return prev;
        const next = [...prev];
        next[idx] = { ...next[idx], name: updated.name, description: updated.description, node_count: updated.nodes?.length ?? 0 };
        return next;
      });
      setSelectedRoadmap((prev) => (prev?.id === updated.id ? updated : prev));
    };
    window.addEventListener('roadmap-updated', handler);
    return () => window.removeEventListener('roadmap-updated', handler);
  }, []);

  // Open specific roadmap if requested from outside (e.g. from DocHub)
  useEffect(() => {
    if (!openRoadmapId || loading) return;
    const existing = roadmaps.find((r) => r.id === openRoadmapId);
    if (existing) {
      openRoadmap(openRoadmapId);
      if (onRoadmapOpened) onRoadmapOpened();
    } else {
      // Fetch it directly
      fetch(`${API_BASE}/api/roadmaps/${openRoadmapId}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((rm) => {
          if (rm) {
            setSelectedRoadmap(rm);
            setView('editor');
            if (onRoadmapOpened) onRoadmapOpened();
          }
        })
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRoadmapId, loading]);

  const openRoadmap = async (roadmapId) => {
    try {
      const res = await fetch(`${API_BASE}/api/roadmaps/${roadmapId}`);
      if (!res.ok) throw new Error(`Failed to load roadmap (${res.status})`);
      setSelectedRoadmap(await res.json());
      setView('editor');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE}/api/roadmaps/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim(), description: '' }),
      });
      if (!res.ok) throw new Error(`Create failed (${res.status})`);
      const rm = await res.json();
      setRoadmaps((prev) => [...prev, rm]);
      setNewName('');
      setCreateOpen(false);
      setSelectedRoadmap(rm);
      setView('editor');
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleEditorSaved = (saved) => {
    setRoadmaps((prev) => {
      const idx = prev.findIndex((r) => r.id === saved.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], name: saved.name, description: saved.description, node_count: saved.nodes?.length ?? 0 };
        return next;
      }
      return prev;
    });
    setSelectedRoadmap(saved);
  };

  const handleEditorDeleted = (roadmapId) => {
    setRoadmaps((prev) => prev.filter((r) => r.id !== roadmapId));
    setSelectedRoadmap(null);
    setView('list');
  };

  if (view === 'aggregate') {
    return <AggregateRoadmapView onBack={() => setView('list')} />;
  }

  if (view === 'editor' && selectedRoadmap) {
    return (
      <RoadmapEditor
        roadmap={selectedRoadmap}
        isReadOnly={isReadOnly}
        onBack={() => setView('list')}
        onSaved={handleEditorSaved}
        onDeleted={handleEditorDeleted}
      />
    );
  }

  // List view
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Roadmaps</Typography>

        <TextField
          size="small"
          placeholder="Search roadmaps…"
          value={searchRoadmaps}
          onChange={(e) => setSearchRoadmaps(e.target.value)}
          sx={{ width: 220 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment>,
            endAdornment: searchRoadmaps ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchRoadmaps('')}><ClearIcon sx={{ fontSize: 14 }} /></IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <Tooltip title="View all roadmaps on one timeline">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AggregateIcon />}
              onClick={() => setView('aggregate')}
              disabled={roadmaps.length === 0}
            >
              All Roadmaps
            </Button>
          </span>
        </Tooltip>

        {!isReadOnly && (
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ bgcolor: '#00897b', '&:hover': { bgcolor: '#00695c' } }}
          >
            New Roadmap
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : roadmaps.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <RoadmapsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">No roadmaps yet.</Typography>
            {!isReadOnly && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
                sx={{ mt: 2 }}
              >
                New Roadmap
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="center">Items</TableCell>
                <TableCell>Updated</TableCell>
                {!isReadOnly && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {roadmaps
                .filter((rm) =>
                  !searchRoadmaps || rm.name.toLowerCase().includes(searchRoadmaps.toLowerCase())
                )
                .map((rm) => (
                  <TableRow
                    key={rm.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openRoadmap(rm.id)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{rm.name}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      {rm.description ? (
                        <Tooltip title={rm.description.length > 40 ? rm.description : ''}>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 280 }}>
                            {rm.description.length > 40
                              ? `${rm.description.slice(0, 40)}…`
                              : rm.description}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={rm.node_count ?? 0} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {rm.updated_at ? new Date(rm.updated_at).toLocaleDateString('en-AU') : '—'}
                      </Typography>
                    </TableCell>
                    {!isReadOnly && (
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Delete roadmap">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                if (window.confirm(`Delete "${rm.name}"?`)) {
                                  fetch(`${API_BASE}/api/roadmaps/${rm.id}`, { method: 'DELETE' })
                                    .then(() => setRoadmaps((prev) => prev.filter((r) => r.id !== rm.id)))
                                    .catch(() => {});
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              {roadmaps.filter((rm) =>
                !searchRoadmaps || rm.name.toLowerCase().includes(searchRoadmaps.toLowerCase())
              ).length === 0 && (
                <TableRow>
                  <TableCell colSpan={isReadOnly ? 4 : 5} align="center">
                    <Typography variant="caption" color="text.secondary">
                      {searchRoadmaps ? `No roadmaps match "${searchRoadmaps}"` : 'No roadmaps yet.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New Roadmap</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
            fullWidth
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !newName.trim()}
            startIcon={creating ? <CircularProgress size={14} /> : <AddIcon />}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export { RoadmapEditor };
export default Roadmap;
